import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import MoreIcon from "../../../../public/images/more.svg";
import DollarCircleIcon from "../../../../public/images/dollar-icon-black.svg";
import CheckIcon from "../../../../public/images/green-check-icon.svg";
import CloseIconDark from "../../../../public/images/close-icon-dark.svg";
import CloseIconRed from "../../../../public/images/close-icon-red.svg";
import ArrowRightIcon from "../../../../public/images/arrow-right.svg";
import LinkSquare from "../../../../public/images/link-square.svg";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import { isRegistrationPaymentLocked } from "app/utils/registrationPaymentUtils";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";
import styles from "./styles.module.scss";

interface RegistrantActionsMenuProps {
  registration: StudentProgramRegistrationModel;
  onRemove: (registration: StudentProgramRegistrationModel) => void;
  onMove?: (registration: StudentProgramRegistrationModel) => void;
  onManagePayment?: (registration: StudentProgramRegistrationModel) => void;
  onMarkAsPaid?: (registration: StudentProgramRegistrationModel) => void;
  onMarkAsUnpaid?: (registration: StudentProgramRegistrationModel) => void;
}

export default function RegistrantActionsMenu({
  registration,
  onRemove,
  onMove,
  onManagePayment,
  onMarkAsPaid,
  onMarkAsUnpaid,
}: RegistrantActionsMenuProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Position is computed in viewport coords and applied via inline `position: fixed`
  // so the dropdown lives in a portal outside the registrants list — opening it
  // can't extend the parent's scroll container.
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const { copyToClipboard } = useCopyToClipboard();

  const paymentLocked = isRegistrationPaymentLocked(registration);
  const invoiceUrl = registration.invoiceUrl;
  const sharePaymentUrl = registration.sharePaymentUrl;

  const handleViewInvoice = () => {
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopyPaymentLink = () => {
    if (sharePaymentUrl) {
      copyToClipboard(sharePaymentUrl);
      toast.success(t("Payment link copied"));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const insideTrigger = menuRef.current?.contains(event.target as Node);
      const insideDropdown = dropdownRef.current?.contains(event.target as Node);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      return undefined;
    }

    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.bottom,
          right: window.innerWidth - rect.right,
        });
      }
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isOpen]);

  const dropdown = isOpen && position && (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={{ position: "fixed", top: position.top, right: position.right, zIndex: 1100 }}
    >
      <ul>
            {!paymentLocked && onManagePayment && (
              <li
                onClick={() => {
                  setIsOpen(false);
                  onManagePayment(registration);
                }}
              >
                <div className={styles.icon_container}>
                  <img src={DollarCircleIcon} alt="" />
                </div>
                {t("Manage Payment")}
              </li>
            )}
            {registration.status !== "on_waitlist" && registration.paymentMethod === "offline" && registration.paymentStatus !== "paid" && onMarkAsPaid && (
              <li
                onClick={() => {
                  setIsOpen(false);
                  onMarkAsPaid(registration);
                }}
              >
                <div className={styles.icon_container}>
                  <img src={CheckIcon} alt="" />
                </div>
                {t("Mark as Paid")}
              </li>
            )}
            {registration.status !== "on_waitlist" && registration.paymentMethod === "offline" && registration.paymentStatus === "paid" && onMarkAsUnpaid && (
              <li
                onClick={() => {
                  setIsOpen(false);
                  onMarkAsUnpaid(registration);
                }}
              >
                <div className={styles.icon_container}>
                  <img src={CloseIconDark} alt="" />
                </div>
                {t("Mark as Unpaid")}
              </li>
            )}
            {invoiceUrl && (
              <li
                onClick={() => {
                  setIsOpen(false);
                  handleViewInvoice();
                }}
              >
                <div className={styles.icon_container}>
                  <img src={LinkSquare} alt="" className={styles.dark_icon} />
                </div>
                {t("View invoice")}
              </li>
            )}
            {sharePaymentUrl && (
              <li
                onClick={() => {
                  setIsOpen(false);
                  handleCopyPaymentLink();
                }}
              >
                <div className={styles.icon_container}>
                  <img src={LinkSquare} alt="" className={styles.dark_icon} />
                </div>
                {t("Copy payment link")}
              </li>
            )}
            {onMove && (
              <li
                onClick={() => {
                  setIsOpen(false);
                  onMove(registration);
                }}
                data-testid="registrant-move-to-program"
              >
                <div className={styles.icon_container}>
                  <img src={ArrowRightIcon} alt="" className={styles.dark_icon} />
                </div>
                {t("Move to another Program")}
              </li>
            )}
            <li
              onClick={() => {
                setIsOpen(false);
                onRemove(registration);
              }}
              data-testid="registrant-remove"
              className={styles.remove_action}
            >
              <div className={styles.icon_container}>
                <img src={CloseIconRed} alt="" />
              </div>
              {t("Remove Registrant")}
            </li>
      </ul>
    </div>
  );

  return (
    <div className={styles.menu_container} ref={menuRef}>
      <button
        ref={triggerRef}
        className={styles.ellipsis_button}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        data-testid="registrant-actions-button"
      >
        <img src={MoreIcon} alt="actions" className={styles.more_icon} />
      </button>
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}
